import { Download, FileText } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { downloadPortalProforma, type PortalProforma } from "@/features/portal/api";
import { portalHref } from "@/features/portal/portal-base";
import { formatPortalDate } from "@/features/portal/portal-format";
import { downloadBlob } from "@/lib/download";
import { formatCents } from "@/lib/utils";

/** Real-data equivalent of the mockup's "what you've paid" card: the proposals
 * (proforma quotes) the firm has shared with this client. */
export function ProposalsCard({ proformas }: { proformas: PortalProforma[] }) {
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  async function handleDownload(p: PortalProforma) {
    setDownloadingId(p.id);
    try {
      downloadBlob(`proposal-${p.invoiceNumber}.pdf`, await downloadPortalProforma(p.id));
    } catch (err) {
      toast.error((err as { message?: string })?.message ?? "Download failed.");
    } finally {
      setDownloadingId(null);
    }
  }

  return (
    <Card className="gap-0 overflow-hidden p-0">
      <div className="flex items-baseline justify-between gap-3 px-6 pt-6 pb-4">
        <h3 className="font-heading text-xl font-bold text-foreground">Proposals from your firm</h3>
        {proformas.length > 0 && (
          <Link to={portalHref("/proposals")} className="text-info text-sm font-semibold hover:underline">
            See all
          </Link>
        )}
      </div>
      {proformas.length === 0 ? (
        <div className="flex flex-col items-center gap-2 px-6 pt-2 pb-10 text-center">
          <div className="bg-accent text-accent-foreground grid size-11 place-items-center rounded-sm">
            <FileText className="size-5" />
          </div>
          <p className="text-muted-foreground text-sm">
            No proposals yet. When your tax team sends one, it'll appear here.
          </p>
        </div>
      ) : (
        <ul className="divide-y">
          {proformas.slice(0, 4).map((p) => (
            <li key={p.id} className="flex flex-wrap items-center justify-between gap-3 px-6 py-4">
              <div className="flex min-w-0 flex-col leading-tight">
                <span className="font-medium text-foreground">Proposal {p.invoiceNumber}</span>
                {p.noteToClient ? (
                  <span className="text-muted-foreground truncate text-sm">{p.noteToClient}</span>
                ) : (
                  <span className="text-muted-foreground text-sm">Dated {formatPortalDate(p.invoiceDate)}</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="font-heading text-lg font-semibold tabular-nums text-foreground">
                  {formatCents(p.totalCents)}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Download proposal ${p.invoiceNumber}`}
                  onClick={() => handleDownload(p)}
                  disabled={downloadingId === p.id}
                >
                  <Download className="size-4" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
